import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  Wallet, CheckCircle2, Circle, Search, FileDown, Banknote,
  TrendingUp, TrendingDown, AlertTriangle, Pencil, X, Save,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const VERIFY_FILTERS = [
  { v: "false", label: "Unverified" },
  { v: "true", label: "Verified" },
  { v: "", label: "All" },
];

const DIR_FILTERS = [
  { v: "", label: "All" },
  { v: "received", label: "Received" },
  { v: "paid", label: "Paid" },
];

const MODE_CLR = {
  cash: "bg-gray-100 text-gray-700",
  upi: "bg-[#DBEAFE] text-[#1D4ED8]",
  bank: "bg-[#DCFCE7] text-[#15803D]",
  cheque: "bg-[#FEF3C7] text-[#B45309]",
  other: "bg-gray-100 text-gray-600",
};

function EditModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: row.date || "",
    amount: row.amount || 0,
    mode: row.mode || "cash",
    reference: row.reference || "",
    notes: row.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/payments/${row.id}`, {
        date: form.date,
        amount: Number(form.amount),
        mode: form.mode,
        reference: form.reference,
        notes: form.notes,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="payment-log-edit">
        <div className="p-5 border-b border-gray-200 flex justify-between items-start">
          <div>
            <div className="small-label text-gray-500">Edit · {row.direction}</div>
            <div className="font-heading font-black text-xl">{row.party_name || "—"}</div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className="small-label block mb-1">Date</label>
            <input type="date" className="input-lg" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-1">Amount ₹</label>
            <input type="number" step="0.01" className="input-lg text-right data-num" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-1">Mode</label>
            <select className="input-lg" value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="small-label block mb-1">Reference</label>
            <input className="input-lg" value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="UTR / cheque no" />
          </div>
          <div className="col-span-2">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-5 flex items-center gap-2"><Save size={16} /> Save</button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentsLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState("false"); // default: unverified
  const [direction, setDirection] = useState("");
  const [mode, setMode] = useState("");
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (verified) params.verified = verified;
      if (direction) params.direction = direction;
      if (mode) params.mode = mode;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/payments/log", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [verified, direction, mode, from, to]);

  const rows = useMemo(() => {
    const r = data?.rows || [];
    const s = q.trim().toLowerCase();
    return s ? r.filter((x) =>
      (x.party_name || "").toLowerCase().includes(s) ||
      (x.reference || "").toLowerCase().includes(s) ||
      (x.notes || "").toLowerCase().includes(s)
    ) : r;
  }, [data, q]);

  const totals = useMemo(() => ({
    received: rows.filter((r) => r.direction === "received").reduce((s, r) => s + (r.amount || 0), 0),
    paid: rows.filter((r) => r.direction === "paid").reduce((s, r) => s + (r.amount || 0), 0),
    verified: rows.filter((r) => r.bank_verified).length,
    unverified: rows.filter((r) => !r.bank_verified && ["bank", "upi", "cheque"].includes(r.mode)).length,
  }), [rows]);

  const toggleVerify = async (row) => {
    try {
      await api.post(`/payments/${row.id}/verify`, { verified: !row.bank_verified });
      toast.success(row.bank_verified ? "Marked unverified" : "✓ Verified against bank statement");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    window.open(`${apiUrl}/api/exports/payments?${p.toString()}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="payments-log">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Payments Log</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Reconcile every payment against your bank statement
        </span>
        <button onClick={exportCsv} className="ml-auto h-9 px-3 bg-black text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5"><FileDown size={13} /> CSV</button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><TrendingUp size={12} /> Total received</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="paylog-received">₹ {formatMoney(totals.received)}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><TrendingDown size={12} /> Total paid</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="paylog-paid">₹ {formatMoney(totals.paid)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#15803D]" /> Verified</div>
          <div className="data-num text-3xl mt-1" data-testid="paylog-verified">{totals.verified}</div>
          <div className="text-xs text-gray-500 mt-1">in this filter</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><AlertTriangle size={12} /> Pending verification</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="paylog-unverified">{totals.unverified}</div>
          <div className="text-xs text-[#92400E] mt-1">bank / UPI / cheque only</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="small-label text-gray-500">Verify:</span>
          {VERIFY_FILTERS.map((f) => (
            <button key={f.v || "all"} onClick={() => setVerified(f.v)}
              className={`px-3 h-9 rounded-sm text-xs font-semibold border ${verified === f.v ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`verify-${f.v || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className="small-label text-gray-500">Direction:</span>
          {DIR_FILTERS.map((f) => (
            <button key={f.v || "all"} onClick={() => setDirection(f.v)}
              className={`px-3 h-9 rounded-sm text-xs font-semibold border ${direction === f.v ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`dir-${f.v || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <select className="input-lg h-9" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="mode-filter">
            <option value="">All modes</option>
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="bank">Bank</option>
            <option value="cheque">Cheque</option>
            <option value="other">Other</option>
          </select>
          <input type="date" className="input-lg h-9" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="paylog-from" />
          <span>→</span>
          <input type="date" className="input-lg h-9" value={to} onChange={(e) => setTo(e.target.value)} data-testid="paylog-to" />
        </div>
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 w-56 pl-8" placeholder="Party / reference / notes…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="paylog-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Party</th>
              <th className="text-left">Direction</th>
              <th className="num">Amount ₹</th>
              <th className="text-left">Mode</th>
              <th className="text-left">Reference</th>
              <th className="text-left">Notes</th>
              <th className="text-left">Verified</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="9" className="py-8 text-center text-gray-400 text-sm">
                <CheckCircle2 className="mx-auto text-[#15803D] mb-2" size={28} />
                <div>Nothing to reconcile in this filter — all caught up.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`paylog-row-${r.id}`} className={r.bank_verified ? "bg-[#F0FDF4]" : ""}>
                <td className="data-num text-xs whitespace-nowrap">{r.date}</td>
                <td>
                  {r.party_id ? (
                    <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                  ) : <span className="text-gray-400">—</span>}
                  {r.party_type && <div className="text-xs text-gray-500 capitalize">{r.party_type}</div>}
                </td>
                <td>
                  <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${r.direction === "received" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
                    {r.direction}
                  </span>
                </td>
                <td className={`num font-bold ${r.direction === "received" ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                  {formatMoney(r.amount)}
                </td>
                <td>
                  <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold uppercase ${MODE_CLR[r.mode] || "bg-gray-100"}`}>{r.mode}</span>
                </td>
                <td className="text-xs">{r.reference || <span className="text-gray-400">—</span>}</td>
                <td className="text-xs text-gray-600 max-w-[200px] truncate" title={r.notes}>{r.notes || <span className="text-gray-400">—</span>}</td>
                <td>
                  <button onClick={() => toggleVerify(r)}
                    className={`text-xs font-semibold flex items-center gap-1.5 px-2 py-1 rounded-sm border ${r.bank_verified ? "bg-[#DCFCE7] text-[#15803D] border-[#15803D]" : "bg-white text-gray-500 border-gray-300 hover:border-black hover:text-black"}`}
                    data-testid={`verify-${r.id}`}>
                    {r.bank_verified
                      ? <><CheckCircle2 size={12} /> Verified</>
                      : <><Circle size={12} /> Mark verified</>
                    }
                  </button>
                  {r.bank_verified && r.bank_verified_at && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      by {r.bank_verified_by} · {r.bank_verified_at.slice(0, 10)}
                    </div>
                  )}
                </td>
                <td className="text-right">
                  {isAdmin && (
                    <button onClick={() => setEditRow(r)} className="text-gray-400 hover:text-black" title="Edit notes" data-testid={`edit-${r.id}`}>
                      <Pencil size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-gray-50">
                <td colSpan="3" className="text-right pr-3">Totals · {rows.length}</td>
                <td className="num">
                  <span className="text-[#15803D]">+{formatMoney(totals.received)}</span>
                  <span className="text-gray-300 mx-1">/</span>
                  <span className="text-[#B91C1C]">-{formatMoney(totals.paid)}</span>
                </td>
                <td colSpan="3"></td>
                <td className="text-xs">
                  <span className="text-[#15803D]">{totals.verified} verified</span>
                  <span className="text-gray-300 mx-1">·</span>
                  <span className="text-[#B45309]">{totals.unverified} pending</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editRow && (
        <EditModal row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />
      )}

      <Banknote size={0} />
    </div>
  );
}
